import React, { useEffect, useState } from 'react';
import { Modal, styles as primStyles } from '../reconciliation/primitives';
import FxRatePicker from './FxRatePicker';

// Shared cover-sheet "Name & Title" modal used by both the VAT package
// generation flow (VatDashboard) and the Luxembourg AP request download flow
// (LuxembourgDashboard). It collects an optional Requestor and Approver name +
// title; leaving either blank lets the backend apply its standard defaults.
//
// When `fxOptions` is supplied the modal also renders an FxRatePicker below the
// name fields, letting the user choose which from_currency→USD rate the
// generated package uses. The chosen rate is folded into the onConfirm payload
// as { fx_rate, fx_rate_date } following the backend contract: fx_rate is sent
// only when the user changed from the dynamic default (picked another date or
// typed an override); fx_rate is null to mean "use the default". A picked date
// sends that date; a free-typed override sends a null date.
//
// Props:
//   open             — whether the modal is rendered.
//   title            — modal heading (e.g. "Generate VAT Package").
//   confirmLabel     — confirm button label when not busy.
//   defaultRequestor — placeholder + prefill default for the Requestor field.
//   initialRequestor — initial Requestor value (falls back to defaultRequestor).
//   initialApprover  — initial Approver value (defaults to blank).
//   fxOptions        — FxOptionsResponse ({ currency, default, current_override,
//                      available }) or null. When null/absent the FX picker is
//                      hidden (e.g. a fresh VAT package has no batch yet, so no
//                      rates can be resolved) and onConfirm receives null fx.
//   payingBanks      — list of active paying banks for the optional
//                      "Transfer funds from" select, or null/absent to hide
//                      it entirely (the Luxembourg flow passes nothing and is
//                      unaffected). Only the VAT package flow supplies this.
//   initialPayingBankId — preselected paying bank id (regenerate prefills the
//                      batch's stored value). '' / null selects "— none —".
//   companyCodes     — list of active JE "Co Code" destination entities for
//                      the optional "Company code" select, or null/absent to
//                      hide it entirely (the Luxembourg flow passes nothing
//                      and is unaffected). Only the VAT package flow supplies
//                      this.
//   initialCompanyCodeId — preselected company code id (regenerate prefills
//                      the batch's stored value). '' / null selects
//                      "— default (1256) —".
//   busy             — disables inputs/buttons and shows a working label.
//   onConfirm        — (requestorName, approverName, fx, payingBankId,
//                      companyCodeId) => void.
//                      Names are the trimmed field contents (may be empty
//                      strings). fx is { fx_rate, fx_rate_date }: both null
//                      when unchanged from the default; otherwise the chosen
//                      rate (number) and the picked date (or null for a
//                      free-typed override). payingBankId is the 4th TRAILING
//                      argument so 3-arg consumers (Luxembourg) are untouched:
//                      undefined when no payingBanks list was supplied, null
//                      when "— none —" is selected, else the selected UUID.
//                      companyCodeId is the 5th TRAILING argument with the
//                      same semantics: undefined when no companyCodes list
//                      was supplied, null when "— default (1256) —" is
//                      selected, else the selected UUID.
//   onClose          — called on cancel / ESC / backdrop click.
export default function RequestorApproverModal({
  open,
  title,
  confirmLabel = 'Generate Package',
  defaultRequestor = '',
  initialRequestor,
  initialApprover,
  fxOptions = null,
  payingBanks = null,
  initialPayingBankId = null,
  companyCodes = null,
  initialCompanyCodeId = null,
  busy = false,
  onConfirm,
  onClose,
}) {
  const [requestor, setRequestor] = useState('');
  const [approver, setApprover] = useState('');
  // Selected "Transfer funds from" paying bank id ('' = none).
  const [payingBankId, setPayingBankId] = useState('');
  // Selected JE "Co Code" destination id ('' = default 1256). The two flags
  // drive the paying-bank soft-link: while the user has never touched the
  // company-code select and it is at its blank default (or a value this
  // soft-link itself set), picking a paying bank auto-selects the company
  // code whose `code` matches the bank's `company_code`. A regenerate's
  // stored preselection is neither, so it is never overridden.
  const [companyCodeId, setCompanyCodeId] = useState('');
  const [companyCodeTouched, setCompanyCodeTouched] = useState(false);
  const [companyCodeAutoLinked, setCompanyCodeAutoLinked] = useState(false);
  // Local FxRatePicker selection: { fx_rate, fx_rate_date, isOverride }. Seeded
  // from fxOptions (preselect the stored override if present, else the default).
  const [fxValue, setFxValue] = useState(null);

  // Re-seed the fields whenever the modal (re)opens or the initial values
  // change — e.g. a regenerate prefilled from the batch's stored values.
  useEffect(() => {
    if (!open) return;
    setRequestor(initialRequestor != null ? initialRequestor : defaultRequestor);
    setApprover(initialApprover != null ? initialApprover : '');
  }, [open, initialRequestor, initialApprover, defaultRequestor]);

  // Re-seed the paying-bank selection on open (regenerate preselects the
  // batch's stored id; generate starts at "— none —").
  useEffect(() => {
    if (!open) return;
    setPayingBankId(initialPayingBankId != null ? String(initialPayingBankId) : '');
  }, [open, initialPayingBankId]);

  // Re-seed the company-code selection on open (regenerate preselects the
  // batch's stored id; generate starts at "— default (1256) —").
  useEffect(() => {
    if (!open) return;
    setCompanyCodeId(initialCompanyCodeId != null ? String(initialCompanyCodeId) : '');
    setCompanyCodeTouched(false);
    setCompanyCodeAutoLinked(false);
  }, [open, initialCompanyCodeId]);

  // Seed the FX selection from the options. A stored override (typed rate, no
  // date) preselects as an override; a stored override with a date preselects
  // that date; otherwise the system default is selected.
  useEffect(() => {
    if (!open) return;
    if (!fxOptions) {
      setFxValue(null);
      return;
    }
    const ov = fxOptions.current_override;
    if (ov && ov.rate_date == null) {
      setFxValue({
        fx_rate: String(ov.rate),
        fx_rate_date: null,
        isOverride: true,
      });
    } else if (ov && ov.rate_date != null) {
      setFxValue({
        fx_rate: Number(ov.rate),
        fx_rate_date: ov.rate_date,
        isOverride: false,
      });
    } else {
      setFxValue({
        fx_rate: fxOptions.default ? Number(fxOptions.default.rate) : null,
        fx_rate_date: fxOptions.default ? fxOptions.default.rate_date : null,
        isOverride: false,
      });
    }
  }, [open, fxOptions]);

  if (!open) return null;

  const handleClose = () => {
    if (busy) return;
    onClose && onClose();
  };

  // Derive what to send to the backend from the current FX selection. Returns
  // { fx_rate, fx_rate_date }: both null when the user is on the unchanged
  // default (or there's no FX picker), so the backend uses the dynamic default.
  const deriveFx = () => {
    if (!fxOptions || !fxValue) return { fx_rate: null, fx_rate_date: null };
    if (fxValue.isOverride) {
      const num = parseFloat(fxValue.fx_rate);
      if (Number.isNaN(num)) return { fx_rate: null, fx_rate_date: null };
      return { fx_rate: num, fx_rate_date: null };
    }
    const def = fxOptions.default;
    // On the unchanged default → send null so the backend uses its dynamic
    // default rather than freezing today's value onto the batch.
    if (
      def &&
      fxValue.fx_rate_date === def.rate_date &&
      Number(fxValue.fx_rate) === Number(def.rate)
    ) {
      return { fx_rate: null, fx_rate_date: null };
    }
    // No default available and nothing picked → nothing to send.
    if (fxValue.fx_rate == null) return { fx_rate: null, fx_rate_date: null };
    return {
      fx_rate: Number(fxValue.fx_rate),
      fx_rate_date: fxValue.fx_rate_date,
    };
  };

  const handleConfirm = () => {
    // 4th trailing arg: undefined when the paying-bank select is not shown
    // (Luxembourg's 3-arg handler simply ignores it), null for "— none —",
    // else the selected paying bank UUID. 5th trailing arg: same semantics
    // for the company code (null = "— default (1256) —").
    const payingBank = payingBanks ? (payingBankId || null) : undefined;
    const companyCode = companyCodes ? (companyCodeId || null) : undefined;
    onConfirm && onConfirm(requestor.trim(), approver.trim(), deriveFx(), payingBank, companyCode);
  };

  // Soft linkage: paying_bank_accounts rows carry a company_code string.
  // When the user picks a bank and the company-code select is still at its
  // untouched default (or only holds a value this link set), auto-select the
  // company code matching the bank's company_code. The user can still
  // override either independently afterwards.
  const handlePayingBankChange = (value) => {
    setPayingBankId(value);
    if (!companyCodes || companyCodeTouched) return;
    if (companyCodeId !== '' && !companyCodeAutoLinked) return;
    const bank = (payingBanks || []).find((b) => String(b.id) === value);
    const match = bank && bank.company_code
      ? companyCodes.find((c) => c.code === bank.company_code)
      : null;
    if (match) {
      setCompanyCodeId(String(match.id));
      setCompanyCodeAutoLinked(true);
    } else if (companyCodeAutoLinked) {
      // The newly-picked bank has no matching code — drop the stale link
      // back to the default rather than keep the previous bank's code.
      setCompanyCodeId('');
      setCompanyCodeAutoLinked(false);
    }
  };

  const payingBankLabel = (bank) => {
    let label = bank.entity_name || '';
    if (bank.bank_name) label += ` — ${bank.bank_name}`;
    if (bank.company_code) label += ` (company code ${bank.company_code})`;
    return label;
  };

  return (
    <Modal
      title={title}
      subtitle="Cover-sheet Requestor & Approver (Name & Title) — optional"
      onClose={handleClose}
      footer={
        <>
          <button
            style={primStyles.ghostBtn}
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            style={{ ...primStyles.primaryBtn, marginLeft: 'auto' }}
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="vat-control-label">Requestor (Name &amp; Title)</span>
          <input
            type="text"
            className="status-select"
            value={requestor}
            onChange={(e) => setRequestor(e.target.value)}
            placeholder={defaultRequestor}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span className="vat-control-label">Approver (Name &amp; Title)</span>
          <input
            type="text"
            className="status-select"
            value={approver}
            onChange={(e) => setApprover(e.target.value)}
            placeholder="Leave blank to use the default approver"
          />
        </label>
        <p style={{ fontSize: 11.5, color: '#6b7280', margin: 0 }}>
          Leaving a field blank applies the standard default.
        </p>
        {payingBanks && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="vat-control-label">Transfer funds from</span>
            <select
              className="status-select"
              value={payingBankId}
              onChange={(e) => handlePayingBankChange(e.target.value)}
              disabled={busy}
            >
              <option value="">— none —</option>
              {payingBanks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {payingBankLabel(bank)}
                </option>
              ))}
            </select>
          </label>
        )}
        {companyCodes && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span className="vat-control-label">Company code</span>
            <select
              className="status-select"
              value={companyCodeId}
              onChange={(e) => {
                setCompanyCodeId(e.target.value);
                setCompanyCodeTouched(true);
                setCompanyCodeAutoLinked(false);
              }}
              disabled={busy}
            >
              <option value="">— default (1256) —</option>
              {companyCodes.map((code) => (
                <option key={code.id} value={code.id}>
                  {`${code.code} — ${code.entity_name}`}
                </option>
              ))}
            </select>
            {/* Placeholder wording — the client sends the final text later. */}
            <p style={{ fontSize: 11.5, color: '#6b7280', margin: 0 }}>
              Which foreign entity this expense is passed to — provided by the international team.
            </p>
          </label>
        )}
        {fxOptions && fxValue && (
          <div
            style={{
              borderTop: '1px solid #e5e7eb',
              paddingTop: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <FxRatePicker
              currency={fxOptions.currency}
              defaultOption={fxOptions.default}
              currentOverride={fxOptions.current_override}
              available={fxOptions.available}
              value={fxValue}
              onChange={setFxValue}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
